// â”€â”€â”€ User audit log writer â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
//
// Append-only audit trail for every contact/user mutation. Read by HR audit
// surfaces. Immutable: once written, never updated or deleted (retention sweep
// in Phase 14 handles old logs).
//
// Firestore collection: `userAuditLogs/`
// Rules: signed-in user can create entry if `performedBy == auth.uid`. Read
// restricted to superadmin. No update/delete (immutable).
//
// Server-side writes (Cloud Functions via admin SDK) bypass rules â€” they can
// set `performedBy` to a contactId or sentinel like `'system:m365-sync'`.

import {
  addDoc,
  collection,
  serverTimestamp,
  getDocs,
  query,
  where,
  orderBy,
  limit,
} from 'firebase/firestore';
import { db } from '../config/firebase';

/** Canonical audit actions. Extend as new mutation types are introduced. */
export type AuditAction =
  // Contact lifecycle
  | 'contact_created'
  | 'contact_updated'
  | 'contact_deactivated'
  | 'contact_reactivated'
  | 'contact_merged'
  | 'contact_deleted'
  // User/auth lifecycle
  | 'promoted_to_user'
  | 'demoted_from_user'
  | 'user_role_changed'
  | 'app_disabled'
  | 'app_enabled'
  | 'access_policy_changed'
  // M365 sync
  | 'm365_synced'
  | 'm365_promoted'
  // Retention (Phase 14)
  | 'contact_anonymized';

/** Apps that can originate an audit entry. `'system'` for unattributed
 *  server-side jobs (M365 sync, retention sweep). */
export type AuditApp = 'account-portal' | 'epr' | 'okr-tracker' | 'kpi' | 'system';

export interface AuditLogEntry {
  /** ContactId of the subject (the contact being mutated). Either this or
   *  `subjectUid` must be set; both is allowed when both are known. */
  subjectContactId?: string;
  /** Firebase Auth uid of the subject (when the mutation is on `users/`). */
  subjectUid?: string;
  action: AuditAction;
  /** auth uid of admin/dispatcher, OR contactId, OR sentinel like
   *  `'system:m365-sync'`. Client-side writes MUST match `auth.uid` per rule. */
  performedBy: string;
  performedFromApp: AuditApp;
  /** Snapshot of changed fields BEFORE mutation. Optional for create actions. */
  before?: Record<string, any>;
  /** Snapshot of changed fields AFTER mutation. Optional for delete actions. */
  after?: Record<string, any>;
  /** Free-text note (e.g. reason for deactivation). */
  notes?: string;
}

const AUDIT_COL = collection(db, 'userAuditLogs');

/** Append an audit log entry. `timestamp` is set server-side.
 *  Fields with `undefined` value are stripped (Firestore rejects them). */
export async function writeAuditLog(entry: AuditLogEntry): Promise<void> {
  const clean = Object.fromEntries(
    Object.entries(entry).filter(([, v]) => v !== undefined),
  );
  await addDoc(AUDIT_COL, {
    ...clean,
    timestamp: serverTimestamp(),
  });
}

// ─── Read queries (superadmin only, per firestore.rules) ──────────────────────

export interface AuditLogRecord extends AuditLogEntry {
  id: string;
  /** Firestore Timestamp. */
  timestamp: any;
}

/** Fetch most-recent audit log entries. Default 200. Superadmin only. */
export async function fetchRecentAuditLogs(pageLimit = 200): Promise<AuditLogRecord[]> {
  const snap = await getDocs(
    query(AUDIT_COL, orderBy('timestamp', 'desc'), limit(pageLimit)),
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as AuditLogRecord));
}

/** Fetch audit logs for a specific subject contact. */
export async function fetchAuditLogsByContact(
  contactId: string,
  pageLimit = 100,
): Promise<AuditLogRecord[]> {
  const snap = await getDocs(
    query(
      AUDIT_COL,
      where('subjectContactId', '==', contactId),
      orderBy('timestamp', 'desc'),
      limit(pageLimit),
    ),
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as AuditLogRecord));
}

/** Fetch audit logs performed by a specific actor (auth uid). */
export async function fetchAuditLogsByActor(
  performedBy: string,
  pageLimit = 100,
): Promise<AuditLogRecord[]> {
  const snap = await getDocs(
    query(
      AUDIT_COL,
      where('performedBy', '==', performedBy),
      orderBy('timestamp', 'desc'),
      limit(pageLimit),
    ),
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as AuditLogRecord));
}

/** Fetch audit logs of a specific action type. */
export async function fetchAuditLogsByAction(
  action: AuditAction,
  pageLimit = 100,
): Promise<AuditLogRecord[]> {
  const snap = await getDocs(
    query(
      AUDIT_COL,
      where('action', '==', action),
      orderBy('timestamp', 'desc'),
      limit(pageLimit),
    ),
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as AuditLogRecord));
}
